/**
 * Coauthor list for post create flows: add/remove and clear when privacy is anonymous.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CoauthorSuggestion } from "components/Coauthors/CoauthorSelect";
import { PrivacyLevel } from "util/userUtils";

export interface UseCoauthorManagerResult {
  coauthors: CoauthorSuggestion[];
  setCoauthors: React.Dispatch<React.SetStateAction<CoauthorSuggestion[]>>;
  addCoauthor: (s: CoauthorSuggestion) => void;
  removeCoauthor: (c: CoauthorSuggestion) => void;
  coauthorPairs: Array<{ id: number; type: string }>;
}

/**
 * @param privacy - When `"anonymous"`, coauthors are cleared to avoid accidental submission.
 */
export function useCoauthorManager(privacy: PrivacyLevel): UseCoauthorManagerResult {
  const [coauthors, setCoauthors] = useState<CoauthorSuggestion[]>([]);

  useEffect(() => {
    if (privacy === "anonymous" && coauthors.length > 0) {
      setCoauthors([]);
    }
  }, [privacy, coauthors.length]);

  const addCoauthor = useCallback((s: CoauthorSuggestion) => {
    setCoauthors((prev) => {
      if (prev.some((c) => c.id === s.id && c.type === s.type)) return prev;
      return [...prev, s];
    });
  }, []);

  const removeCoauthor = useCallback((c: CoauthorSuggestion) => {
    setCoauthors((prev) => prev.filter((p) => !(p.id === c.id && p.type === c.type)));
  }, []);

  const coauthorPairs = useMemo(
    () => coauthors.map((c) => ({ id: c.id, type: c.type })),
    [coauthors]
  );

  return {
    coauthors,
    setCoauthors,
    addCoauthor,
    removeCoauthor,
    coauthorPairs,
  };
}
